import { ___p, _js } from '@ifixit/utils/localize';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from '@ifixit/ui/components/alert-dialog';
import { Badge } from '@ifixit/ui/components/badge';
import { Checkbox } from '@ifixit/ui/components/checkbox';
import { FaIcon } from '@ifixit/ui/components/icon';
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogTitle,
} from '@ifixit/ui/components/dialog';
import {
   Pagination,
   PaginationContent,
   PaginationItem,
   PaginationLink,
   PaginationNext,
   PaginationPrevious,
   calculatePageRange,
} from '@ifixit/ui/components/pagination';
import {
   Select,
   SelectContent,
   SelectGroup,
   SelectItem,
   SelectLabel,
   SelectSeparator,
   SelectTrigger,
   SelectValue,
} from '@ifixit/ui/components/select';
import { Skeleton } from '@ifixit/ui/components/skeleton';
import { Switch } from '@ifixit/ui/components/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { toastAt } from '@ifixit/ui/components/toast';
import { cn } from '@ifixit/ui/lib/utils';
import {
   type MouseEvent as ReactMouseEvent,
   type ReactNode,
   useEffect,
   useId,
   useRef,
   useState,
} from 'react';
import { EditorFooter } from 'Shared/editor/editor_footer';
import { MessageCard } from 'Shared/editor/message_card';
import { isShortcutClaimed } from 'Shared/editor/shortcut_scope';
import { ShortcutList } from 'Guide/Edit/v2/KeyboardShortcutsDialog';
import type {
   ModerationContent,
   ModerationEntry,
   ModerationFilter,
   ModerationLangFilter,
   ModerationPageData,
   ModerationProps,
   ModerationQueueKey,
   ModerationTopVoter,
   ModerationView,
   ModerationVoter,
} from './moderation_types';
import {
   moderationPageKey,
   useModerationPage,
   useVoteActions,
   type VoteOutcome,
} from './use_moderation_api';

// The page's toasts sit at the top, where they always have. The page's one
// Toaster is in the bottom right.
const toast = toastAt('top-center');

/** The two directions a moderator vote can go, as ModeratorVoteLib scores
 *  them. */
const UP_VOTE = 1;
const DOWN_VOTE = -1;

/** The langFilter value that turns the language filter off, and the URL
 *  param it rides in. Patrol uses the same param name with different
 *  values (its fixed three, not a langid); only 'all' means the same
 *  thing on both pages. */
const ALL_LANGUAGES = 'all';
const LANGUAGE_FILTER_PARAM = 'langFilter';

/**
 * The /Moderation queue: posts and comments the community flagged, each
 * waiting for enough votes to carry out what was proposed or to turn it
 * down. The shell, card and list grammar mirror /Translate and
 * /Leaderboard — sibling tabs in the community nav — so the pages read as
 * one surface.
 */

/** The two strips a card closes on. A step darker than the gray-50 page,
 *  which they matched, so the card's ends dissolved into it.
 *
 *  Text on them floors at gray-600. The strip is 12px, and gray-500 reads
 *  4.39:1 on gray-100 against the 4.5:1 WCAG 1.4.3 wants; gray-600 is
 *  6.87:1. The same words cleared it on the white and gray-50 they used to
 *  sit on, at 4.83:1 and 4.63:1. */
const CARD_STRIP = 'bg-gray-100';

/** One vote, one card, in the editors' SectionCard weight. A stack of
 *  these, not rows in one container: the gap is what separates decisions. */
const CARD_CLASS =
   'overflow-hidden rounded-2xl border border-gray-300 bg-white max-md:-mx-4 max-md:rounded-none max-md:border-x-0';

export function ModerationDashboard(props: ModerationProps) {
   const [view, setView] = useQueueView(props);
   const isArrivingView =
      view.queue === props.activeQueue &&
      view.page === props.initialData.page &&
      view.filter === props.initialData.filter &&
      view.langFilter === props.initialData.langFilter &&
      view.voteid === props.voteid;
   const query = useModerationPage(view, isArrivingView ? props.initialData : undefined);
   const singleVote = view.voteid !== null;
   const pageData = query.data;
   const [superPowers, setSuperPowers] = useState(false);
   const { castVote, cancelVote } = useVoteActions(moderationPageKey(view));
   // The optimistic patch answers the click; this closes the moment before
   // it lands, when a second click or keypress would submit twice.
   const busyVoteid =
      (castVote.isPending ? castVote.variables?.voteid : undefined) ??
      (cancelVote.isPending ? cancelVote.variables?.voteid : undefined) ??
      null;

   // What the keys and the votes did, for a reader who cannot see the ring
   // move or the tally change. Its own line rather than the count under
   // the list, which only changes when the page does.
   const [status, setStatus] = useState('');
   const announce = (message: string) => setStatus(message);

   // A vote that settles one takes its card out of the queue, so the toast
   // is the only thing left saying what the click did. It reads the answer
   // rather than the button: pressing the pass side can still end in a
   // refusal, once the tally is counted.
   const report = (entry: ModerationEntry) => ({
      onSuccess: (result: VoteOutcome) => {
         switch (result.outcome) {
            // A vote that leaves the card in the queue changes the tally and
            // closes the card, which a sighted reader sees and a screen
            // reader does not: the live line says it in two words.
            case 'recorded': {
               announce(_js('Vote recorded'));

               break;
            }
            case 'canceled': {
               announce(_js('Vote withdrawn'));

               break;
            }
            case 'closed': {
               toast.warning(_js('Somebody decided this one first.'));

               break;
            }
            case 'passed': {
               toast.success(_js('Done: %1', entry.actionText));

               break;
            }
            case 'failed': {
               toast.success(_js('Turned down: %1', entry.actionText));

               break;
            }
            // No default
         }
      },
      onError: (error: unknown) => toast.error(voteErrorMessage(error)),
   });

   const vote = (entry: ModerationEntry, score: number) => {
      // The live line only speaks when its words change, so a second
      // "Vote recorded" in a row would be silent. Emptying it on the
      // click, a render before the answer arrives, makes each outcome a
      // change.
      setStatus('');
      castVote.mutate({ voteid: entry.voteid, score, adminForce: superPowers }, report(entry));
      // Working the queue is one decision after another, and the card just
      // decided closes under the pointer. The next one that still wants
      // this reader takes the ring, which scrolls it into view and puts P
      // and F on it, so a pass down the page never needs the mouse.
      const next = nextToVote(pageData?.entries ?? [], entry.voteid);
      if (next) {
         focusOn(next.voteid);
      }
   };

   const undoVote = (entry: ModerationEntry) => {
      if (entry.voteid === busyVoteid) {
         return;
      }

      setStatus('');
      // Withdrawing can only end in 'canceled' or 'closed', so the settled
      // wording never reaches the screen from here.
      cancelVote.mutate({ voteid: entry.voteid }, report(entry));
      // The button just pressed is on the closed card, which the undo
      // opens back up: the ring, and focus with it, stay on that card.
      focusOn(entry.voteid);
   };

   // The dialog is held here rather than per card so the P key reaches the
   // same confirmation the button does.
   const [confirming, setConfirming] = useState<ModerationEntry | null>(null);
   const requestVote = (entry: ModerationEntry, score: number) => {
      if (entry.voteid === busyVoteid) {
         return;
      }

      // Super powers decide the vote outright, so an armed pass click
      // confirms even where the action itself could be undone by hand.
      if (
         score === UP_VOTE &&
         carriesOut(entry, superPowers) &&
         (entry.destructive || superPowers)
      ) {
         setConfirming(entry);
         return;
      }

      vote(entry, score);
   };

   const [helpOpen, setHelpOpen] = useState(false);
   // Only the pending queue has anything to vote on, and only a member with
   // the reputation for it: everyone else gets no keys and no offer of them.
   const shortcutsEnabled = view.queue === 'pending' && (pageData?.canVote ?? false);
   const { focused, focusOn } = useQueueShortcuts({
      entries: pageData?.entries ?? [],
      enabled: shortcutsEnabled,
      onVote: requestVote,
      onHelp: () => setHelpOpen(true),
   });

   // Where the keys are, for a reader who cannot see the ring move. Read
   // off the ring rather than stored, so it can never say a card the ring
   // has left.
   const entries = pageData?.entries ?? [];
   const focusedEntry = entries[focused];
   const whereTheKeysAre = focusedEntry
      ? _js(
           'Card %1 of %2: %3',
           String(focused + 1),
           String(entries.length),
           focusedEntry.actionText
        )
      : '';

   const goTo = (next: Partial<ModerationView>) => {
      // Any navigation leaves the permalink: the queues are what the
      // tabs, filter and pager move between.
      setView({ ...view, voteid: null, ...next });
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
   };

   return (
      <div className="mx-auto w-full max-w-5xl px-4 pb-10 md:px-8">
         <header className="pt-6">
            {/* The language filter sits beside the H1, above the
                description, where the redesigned Patrol put the same
                control: the two review surfaces read as one system. */}
            <div className="flex flex-wrap items-center justify-between gap-4">
               <h1 className="text-[30px] font-medium text-gray-950">{_js('Moderation')}</h1>
               <LanguageFilter
                  langFilter={view.langFilter}
                  viewLanguage={props.viewLanguage}
                  otherLanguages={props.otherLanguages}
                  onSelect={langFilter => goTo({ langFilter, page: 1 })}
               />
            </div>
            <p className="mt-2 max-w-2xl text-sm text-gray-700">
               {_js(
                  'Help make iFixit awesome by removing off-topic or unhelpful posts and comments. Anyone with at least %1 reputation points can vote.',
                  String(props.initialData.reputationThreshold)
               )}{' '}
               <a
                  href={props.helpUrl}
                  className="whitespace-nowrap font-medium text-brand-500 underline-offset-2 hover:text-brand-600 hover:underline"
               >
                  {_js('How moderation works')}
               </a>
            </p>
         </header>

         <div className="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <QueueTabs
               queues={props.queues}
               activeQueue={singleVote ? null : view.queue}
               viewUrl={queue =>
                  viewUrl(props, {
                     queue,
                     page: 1,
                     filter: 'all',
                     langFilter: view.langFilter,
                     voteid: null,
                  })
               }
               onSelect={singleVote ? undefined : queue => goTo({ queue, page: 1, filter: 'all' })}
            />
            {props.isLoggedIn && view.queue === 'pending' && (
               <VotedFilter filter={view.filter} onSelect={filter => goTo({ filter, page: 1 })} />
            )}
         </div>

         {pageData?.canForceVote && (
            // Its own row: it is not a way of reading the queue but a way
            // of deciding it, and the toolbar is for the former. It sits
            // under the filter, which is on the right where the row fits
            // and on the left once the toolbar has wrapped.
            <div className="mt-2 flex max-md:justify-start md:justify-end">
               <SuperPowers enabled={superPowers} onChange={setSuperPowers} />
            </div>
         )}

         {singleVote && (
            <p className="mt-4 text-sm text-gray-700">
               {_js('You are looking at one flagged post.')}{' '}
               <a
                  href={props.queues[0]?.url ?? '/Moderation'}
                  className="font-medium text-brand-500 underline-offset-2 hover:text-brand-600 hover:underline"
               >
                  {_js('Back to the queue')}
               </a>
            </p>
         )}

         <section className="mt-4">
            <QueueBody
               query={query}
               pageData={pageData}
               queue={view.queue}
               langFilter={view.langFilter}
               voting={{
                  isLoggedIn: props.isLoggedIn,
                  loginUrl: props.loginUrl,
                  reputationThreshold: props.initialData.reputationThreshold,
                  viewerReputation: pageData?.viewerReputation ?? null,
               }}
               focused={focused}
               busyVoteid={busyVoteid}
               onVote={requestVote}
               onUndo={undoVote}
            />
         </section>
         {/* One short line, rather than a live region around the list:
             announcing the region would read all ten flagged posts again
             every time the page or the queue changed. */}
         <p aria-live="polite" className="sr-only">
            {pageData && !query.isPlaceholderData
               ? ___p(
                    pageData.entries.length,
                    '%1 flagged post, page %2',
                    '%1 flagged posts, page %2',
                    String(pageData.entries.length),
                    String(view.page)
                 )
               : _js('Loading')}
         </p>
         <p aria-live="polite" className="sr-only">
            {whereTheKeysAre}
         </p>
         <p aria-live="polite" className="sr-only">
            {status}
         </p>

         {pageData && (
            <QueuePagination
               view={view}
               pageData={pageData}
               pageUrl={page => viewUrl(props, { ...view, page })}
               onPageChange={page => goTo({ page })}
            />
         )}

         {/* Under the queue, not over it: a moderator arrives to work the
             cards, and the first band of the page used to be who else had.
             It still says somebody is working this queue, as the page's
             last word rather than its first. */}
         <TopVoters voters={props.topVoters} leaderboardUrl={props.leaderboardUrl} />

         {shortcutsEnabled && (
            <EditorFooter>
               <button
                  type="button"
                  onClick={() => setHelpOpen(true)}
                  aria-label={_js('Keyboard shortcuts')}
                  // Physical keyboards are a desktop affordance; hide the
                  // shortcuts entry on phones and tablets, where it's noise.
                  className={cn(
                     'hidden rounded-sm text-gray-500 hover:text-gray-700 lg:block',
                     FOCUS_RING
                  )}
               >
                  <FaIcon name="keyboard" className="size-4" />
               </button>
            </EditorFooter>
         )}

         <ConfirmDecision
            entry={confirming}
            superPowers={superPowers}
            onOpenChange={open => !open && setConfirming(null)}
            onConfirm={entry => {
               setConfirming(null);
               vote(entry, UP_VOTE);
            }}
         />
         <ShortcutsDialog open={helpOpen} onOpenChange={setHelpOpen} />
      </div>
   );
}

/**
 * The next card in the queue that still wants this reader's vote, starting
 * below the one they just decided and wrapping to the top, since the ones
 * above may have been skipped rather than voted on.
 */
function nextToVote(entries: ModerationEntry[], from: number): ModerationEntry | null {
   const at = entries.findIndex(entry => entry.voteid === from);
   const wants = (entry: ModerationEntry) => entry.canVote && entry.myVote === null;

   for (let step = 1; step <= entries.length; step++) {
      const entry = entries[(at + step) % entries.length];
      if (entry && entry.voteid !== from && wants(entry)) {
         return entry;
      }
   }

   return null;
}

/** Whether this click does the thing rather than counting toward it. */
function carriesOut(entry: ModerationEntry, superPowers: boolean): boolean {
   return superPowers || entry.score + UP_VOTE >= entry.passThreshold;
}

/** What every card needs to say where the voting is and why it is or
 *  isn't open to the reader. */
interface VotingContext {
   isLoggedIn: boolean;
   loginUrl: string;
   reputationThreshold: number;
   viewerReputation: number | null;
}

function QueueBody({
   query,
   pageData,
   queue,
   langFilter,
   voting,
   focused,
   busyVoteid,
   onVote,
   onUndo,
}: {
   query: ReturnType<typeof useModerationPage>;
   pageData: ModerationPageData | undefined;
   /** Which listing this is: an empty one says different things on each. */
   queue: ModerationQueueKey;
   /** Which language it is narrowed to, for the same reason. */
   langFilter: ModerationLangFilter;
   voting: VotingContext;
   focused: number;
   busyVoteid: number | null;
   onVote: (entry: ModerationEntry, score: number) => void;
   onUndo: (entry: ModerationEntry) => void;
}) {
   if (query.isError) {
      return (
         <div className={CARD_CLASS} role="alert">
            <MessageCard
               icon="triangle-exclamation"
               tone="failure"
               title={_js("This queue didn't load")}
               text={_js('Reload the page if this keeps happening.')}
               action={
                  <button
                     type="button"
                     className="button-md button-outline max-md:min-h-11"
                     disabled={query.isFetching}
                     onClick={() => void query.refetch()}
                  >
                     {query.isFetching ? _js('Trying again...') : _js('Try again')}
                  </button>
               }
            />
         </div>
      );
   }

   // keepPreviousData holds the queue you came from on screen while the
   // next one loads, which reads as nothing having happened when you click
   // a tab. The cards you are looking at are the wrong queue's, so they
   // give way to their own shape until the right ones arrive.
   if (!pageData || query.isPlaceholderData) {
      return <QueueSkeleton rows={pageData ? pageData.entries.length : 3} />;
   }

   if (pageData.entries.length === 0) {
      // "Every flagged post has been decided" is the wrong sentence on the
      // queue that lists your own votes, where an empty page means you
      // have not cast one. The language picker sits above all four tabs
      // and narrows this queue too, so on any language but "all" the
      // sentence can only speak for that language: an account with
      // hundreds of English votes opens the German page to this.
      const empty =
         queue === 'mine'
            ? {
                 // Not "No votes yet": that is the card's line for a vote
                 // nobody has weighed in on, and _js keys on the English.
                 title: _js('No votes cast'),
                 text:
                    langFilter === ALL_LANGUAGES
                       ? _js("You haven't voted on anything yet.")
                       : _js("You haven't voted on anything in this language yet."),
                 // Having cast nothing is a starting point, not a job
                 // finished, so it does not get the green tick.
                 icon: 'check-to-slot',
                 tone: 'info' as const,
              }
            : {
                 title: _js('Nothing waiting'),
                 text: _js('Every flagged post in this queue has been decided.'),
                 icon: 'check',
                 tone: 'done' as const,
              };

      return (
         <div className={CARD_CLASS}>
            <MessageCard
               icon={empty.icon}
               tone={empty.tone}
               title={empty.title}
               text={empty.text}
            />
         </div>
      );
   }

   return (
      // The gap is what separates one decision from the next. At 12px it
      // lost to the borders inside the cards; at 24px it still had to
      // carry more than it could once those borders were gone.
      <ul aria-label={_js('Flagged posts')} className="m-0 grid list-none gap-8 p-0">
         {pageData.entries.map((entry, index) => (
            <VoteCard
               key={entry.voteid}
               entry={entry}
               viewerid={pageData.viewerid}
               voting={voting}
               focused={index === focused}
               busy={entry.voteid === busyVoteid}
               onVote={onVote}
               onUndo={onUndo}
            />
         ))}
      </ul>
   );
}

function VoteCard({
   entry,
   viewerid,
   voting,
   focused,
   busy,
   onVote,
   onUndo,
}: {
   entry: ModerationEntry;
   viewerid: number | null;
   voting: VotingContext;
   focused: boolean;
   busy: boolean;
   onVote: (entry: ModerationEntry, score: number) => void;
   onUndo: (entry: ModerationEntry) => void;
}) {
   const card = useRef<HTMLLIElement | null>(null);
   // A card you have voted on closes: the queue is worked ten at a time,
   // and one that still needs you should not have to be told apart from
   // one that is done. Opening it again is a click, and the state is the
   // vote rather than the click, so a reload closes it again.
   const [open, setOpen] = useState(false);
   const settled = entry.status === 'pending' && entry.myVote !== null && !open;

   // Walking the queue from the keyboard has to bring the card with it,
   // 'nearest' so a card already in view doesn't jump. The card takes real
   // focus as well as the ring: a screen reader then follows the keys, the
   // confirm dialog has a card to hand focus back to, and the button a
   // vote unmounts leaves focus on the next card rather than on the body.
   useEffect(() => {
      if (focused) {
         card.current?.focus?.({ preventScroll: true });
         card.current?.scrollIntoView?.({ block: 'nearest' });
      }
   }, [focused]);

   if (settled) {
      return (
         <li
            ref={card}
            {...focusedCardProps(focused)}
            className={cn(
               CARD_CLASS,
               'p-4',
               // The same tone as the mark beside the heading and the pips
               // above it. Green here meant a card was on its way to
               // deleting somebody's writing while the mark inside it was
               // red, which is the pair the pips moved off green for.
               VOTE_TONES[sideTone(entry.myVote ?? 0, entry.destructive)].surface,
               focused && FOCUSED_CARD_CLASS
            )}
         >
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
               {/* The mark sits beside the heading rather than inside it:
                   heading navigation should hear the action, not be led
                   into it by how the reader voted. */}
               <div className="flex min-w-0 items-center gap-2 max-md:w-full">
                  <VoteMark score={entry.myVote ?? 0} destructive={entry.destructive} />
                  <h2 className="m-0 min-w-0 truncate text-base font-semibold text-gray-950">
                     {entry.actionText}
                     <HeadingSuffix entry={entry} />
                  </h2>
               </div>
               <VoteTally entry={entry} viewerid={viewerid} />
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
               <span className="min-w-0 flex-1 truncate text-xs text-gray-600">
                  {reference(entry)}
               </span>
               <div className="flex shrink-0 gap-2 max-md:w-full">
                  <button
                     type="button"
                     className="button-sm button-outline max-md:min-h-11 max-md:flex-1"
                     onClick={() => setOpen(true)}
                  >
                     {_js('Show details')}
                  </button>
                  <button
                     type="button"
                     className="button-sm button-outline max-md:min-h-11 max-md:flex-1"
                     disabled={busy}
                     onClick={() => onUndo(entry)}
                  >
                     {_js('Undo my vote')}
                  </button>
               </div>
            </div>
         </li>
      );
   }

   return (
      <li
         ref={card}
         {...focusedCardProps(focused)}
         className={cn(CARD_CLASS, 'px-4 pt-4', focused && FOCUSED_CARD_CLASS)}
      >
         {/* The tally never shrinks, and it is a sentence now rather than a
             fraction, so on a phone it would crush the heading into one
             word per line. They take a line each below md instead. */}
         <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
            <div className="flex min-w-0 items-center gap-3 max-md:w-full">
               <ActionIcon action={entry.action} destructive={entry.destructive} />
               <h2 className="m-0 min-w-0 text-base font-semibold text-gray-950 md:text-lg">
                  {entry.actionText}
                  <HeadingSuffix entry={entry} />
               </h2>
            </div>
            <VoteTally entry={entry} viewerid={viewerid} />
         </div>

         {/* The question an answer hangs off is orientation, not the thing
             being voted on. As a second bordered card it read as a second
             decision; as a strip it reads as the address of the first. */}
         {entry.context && <ContextStrip content={entry.context} url={entry.url} />}

         <div className="mt-3">
            <SubjectCard
               content={entry.subject}
               url={entry.url}
               openText={entry.openText}
               flagged={entry.status === 'pending'}
            />
         </div>

         {/* One strip, not two. Where the vote stands and what you can do
             about it were a band each, with a rule between them, and
             between them they did one job. The strip runs to the card's
             edges and carries its own padding, so it closes the card
             evenly. */}
         <footer
            className={cn(
               '-mx-4 mt-3 border-t border-gray-200 px-4 py-3 text-xs text-gray-600',
               CARD_STRIP
            )}
         >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
               <span>{_js('Flagged %1', entry.flaggedLabel)}</span>
               <Voters entry={entry} viewerid={viewerid} destructive={entry.destructive} />
               <VoteState entry={entry} viewerid={viewerid} voting={voting} />
               {entry.status === 'pending' && (
                  // The two choices share the line where they fit and take
                  // one each where they don't: the labels are whole
                  // sentences, they never wrap inside a button, and the
                  // card clips whatever runs past its edge.
                  <div className="flex shrink-0 flex-wrap gap-2 max-md:order-last max-md:w-full md:ml-auto md:justify-end">
                     <VoteActions
                        entry={entry}
                        voting={voting}
                        busy={busy}
                        onVote={onVote}
                        onUndo={onUndo}
                        onCollapse={entry.myVote === null ? undefined : () => setOpen(false)}
                     />
                  </div>
               )}
            </div>
         </footer>
      </li>
   );
}

/** Ten cards can share one action, so heading navigation hears ten
 *  identical headings without this. Open or closed, the card is the same
 *  heading. */
function HeadingSuffix({ entry }: { entry: ModerationEntry }) {
   return (
      <span className="sr-only">
         {entry.subject.author
            ? _js(
                 'on %1 by %2',
                 entry.subject.title ?? entry.subject.kind,
                 entry.subject.author.name
              )
            : _js('on %1', entry.subject.title ?? entry.subject.kind)}
      </span>
   );
}

/** The card the keys are on. DESIGN.md's selected row: a brand ring, not a
 *  fill, so the card keeps its surface. Outside the border rather than
 *  inset, where the card's own strips painted over it. */
const FOCUSED_CARD_CLASS = 'border-brand-500 ring-1 ring-brand-500 outline-none';

/** Focusable, but only once the keys have picked it, so Tab never lands
 *  on a card by itself. */
function focusedCardProps(focused: boolean) {
   return focused ? { tabIndex: -1, 'aria-current': 'true' as const } : {};
}

/**
 * The question an answer or comment hangs off, as one line of metadata
 * rather than a card of its own.
 *
 * It used to be built exactly like the flagged post: same header, same
 * border, same prose, told apart only by a paler surface. Two blocks of
 * prose stacked and built the same way left the reader working out which
 * of them the vote was about. The title is what a moderator needs from it
 * (it says whether the flag makes sense), so the title stays and gets
 * room to wrap, and the words behind it stay one click away.
 */
function ContextStrip({ content, url }: { content: ModerationContent; url: string }) {
   return (
      // One shape for both posts below md: who wrote it, then what they
      // wrote, then the control. The order flips at md, where the whole
      // strip fits on one line and the title reads better next to the
      // badge than under it.
      <div
         className={cn(
            '-mx-4 mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-y border-gray-200 px-4 py-2 text-xs text-gray-600',
            CARD_STRIP
         )}
      >
         <Badge className="badge-md badge-gray">{content.kind}</Badge>
         {content.title && (
            // Not font-semibold: it is no longer a heading, and a clipped
            // question title is what made the old card ambiguous, so it
            // wraps rather than truncating.
            <a
               href={url}
               className="text-sm font-medium text-gray-900 no-underline hover:text-brand-500 max-md:order-2 max-md:w-full"
            >
               {content.title}
            </a>
         )}
         <span className="flex items-center gap-x-2 max-md:order-1">
            {content.author && (
               <>
                  <span className="text-gray-400 max-md:hidden" aria-hidden="true">
                     ·
                  </span>
                  <a
                     href={content.author.url}
                     className="flex items-center gap-1.5 font-medium text-gray-900 no-underline hover:text-brand-500"
                  >
                     <img
                        src={content.author.avatar}
                        alt=""
                        className="size-4 shrink-0 rounded-full object-cover"
                        loading="lazy"
                     />
                     {content.author.name}
                  </a>
                  {/* The thread author's standing decides no vote here, and
                      on a phone it is the widest thing on the line. */}
                  <span className="text-gray-400 max-md:hidden" aria-hidden="true">
                     ·
                  </span>
                  <span className="max-md:hidden">
                     {_js('%1 rep', String(content.author.reputation))}
                  </span>
               </>
            )}
            {content.dateLabel && (
               <>
                  <span className="text-gray-400" aria-hidden="true">
                     ·
                  </span>
                  <span>{content.dateLabel}</span>
               </>
            )}
         </span>
         <ClosedProse
            html={content.text}
            // order-last keeps the revealed words below the strip: the
            // body precedes its button in the DOM, and without this it
            // would open between the byline and the control.
            className={cn('order-last w-full pt-1', PROSE_QUIET)}
            buttonClassName="mt-0 max-md:order-3 max-md:w-full max-md:text-left md:ml-auto"
         />
      </div>
   );
}

/**
 * The post being voted on: who wrote it, then what they wrote, then the
 * way out to the thread. The same three parts as the strip above it, in
 * the same order, so the two read as one grammar.
 */
function SubjectCard({
   content,
   url,
   openText,
   flagged,
}: {
   content: ModerationContent;
   url: string;
   openText: string;
   /** Pending, so the badge names it as the flagged object. A decided
    *  vote has nothing left to flag. */
   flagged: boolean;
}) {
   return (
      // No box. The post used to need one to be told from the question it
      // hangs off, and the question is a grey strip now: the flagged post
      // is the only white band on the card, between two grey ones, so the
      // frame is drawn by its neighbours.
      <article className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
         {/* All this badge says is which of the two posts on the card is
             the one being voted on. Red is the destructive tier and the
             pass pips already spend it; amber is the warning tier, and
             most of the queue is reclassification, which warns nobody.
             Gray is the context strip's voice, so the two would look
             alike again. That leaves brand. */}
         <Badge className={cn('badge-md', flagged ? 'badge-brand' : 'badge-gray')}>
            {content.kind}
         </Badge>
         {content.author && (
            <>
               <a
                  href={content.author.url}
                  className="flex items-center gap-1.5 font-semibold text-gray-900 no-underline hover:text-brand-500"
               >
                  <img
                     src={content.author.avatar}
                     alt=""
                     className="size-5 shrink-0 rounded-full object-cover"
                     loading="lazy"
                  />
                  {content.author.name}
               </a>
               <span className="text-gray-400 max-md:hidden">·</span>
               <span className="max-md:hidden">
                  {_js('%1 rep', String(content.author.reputation))}
               </span>
            </>
         )}
         {content.dateLabel && (
            <>
               <span className="text-gray-400">·</span>
               <span>{content.dateLabel}</span>
            </>
         )}
         {/* Right of the byline where the line has room. Below md it goes
             under the words it opens, so the post reads straight down. */}
         <a
            href={url}
            className="text-brand-500 no-underline hover:underline max-md:order-last md:ml-auto"
         >
            {openText}
         </a>
         {/* The byline is 12px because it is labelling; the post is not. */}
         <div className="w-full pt-2 text-sm text-gray-800">
            {content.title && (
               <a
                  href={url}
                  className="mb-1 block font-semibold text-gray-950 no-underline hover:text-brand-500"
               >
                  {content.title}
               </a>
            )}
            {/* The server hands over transformed markup, the same it feeds
                the post page. */}
            <FoldedProse
               html={content.text}
               fold="max-h-64"
               className={cn('prose prose-default', PROSE_HEADINGS)}
            />
         </div>
      </article>
   );
}

/** The prose a post carries can hold headings of its own — "Update
 *  (06/29/26)" and the like — and prose sizes those for a page, not for a
 *  card in a queue, where they end up louder than the card's own title. */
const PROSE_HEADINGS =
   '[&_:is(h1,h2,h3,h4,h5,h6)]:my-2 [&_:is(h1,h2,h3,h4,h5,h6)]:text-sm [&_:is(h1,h2,h3,h4,h5,h6)]:font-semibold';

const PROSE_QUIET_TEXT = 'text-sm text-gray-600';
const PROSE_QUIET = cn('prose prose-default', PROSE_HEADINGS, PROSE_QUIET_TEXT);

/** DESIGN.md's shared focus ring, in one place. */
const FOCUS_RING =
   'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]';

/** brand-600, not 500: the strips these sit on are gray-100, where
 *  brand-500 reads 4.47:1 at 12px against the 4.5:1 WCAG 1.4.3 wants. */
const DISCLOSURE_CLASS = cn(
   'mt-2 rounded-sm text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline',
   FOCUS_RING
);

/** Put away whatever its length, unlike the fold below, which only takes a
 *  control once there is something to unfold. */
function ClosedProse({
   html,
   className,
   buttonClassName,
}: {
   html: string;
   className?: string;
   buttonClassName?: string;
}) {
   const [open, setOpen] = useState(false);
   const bodyId = useId();

   if (html === '') {
      return null;
   }

   return (
      <>
         <div
            id={bodyId}
            hidden={!open}
            className={className}
            dangerouslySetInnerHTML={{ __html: html }}
         />
         <button
            type="button"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen(!open)}
            className={cn(DISCLOSURE_CLASS, buttonClassName)}
         >
            {open ? _js('Show less') : _js('Show more')}
         </button>
      </>
   );
}

/** Which way the reader went, in the heading of a card they have closed. */
function VoteMark({ score, destructive }: { score: number; destructive: boolean }) {
   return (
      <span
         className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-full text-white',
            VOTE_TONES[sideTone(score, destructive)].fill
         )}
      >
         <FaIcon name={score > 0 ? 'check' : 'xmark'} className="size-2.5" />
         <span className="sr-only">
            {score > 0 ? _js('You voted in favour') : _js('You voted against')}
         </span>
      </span>
   );
}

/**
 * What a closed card was about, in one line: enough to know which post it
 * was without opening it again. The question's title names the thread the
 * flagged post sits in, which is what the card shows when it is open.
 */
function reference(entry: ModerationEntry): string {
   const kind = entry.subject.kind;
   const author = entry.subject.author?.name;
   const title = entry.context?.title ?? entry.subject.title;

   if (author && title) {
      return _js('%1 by %2 on %3', kind, author, title);
   }
   if (author) {
      return _js('%1 by %2', kind, author);
   }

   return title ? _js('%1 on %2', kind, title) : kind;
}

/**
 * Only what overruns the fold gets a control, so the short posts, which
 * are most of them, read exactly as they did.
 */
function FoldedProse({
   html,
   fold,
   className,
}: {
   html: string;
   fold: string;
   className?: string;
}) {
   const body = useRef<HTMLDivElement | null>(null);
   const [overruns, setOverruns] = useState(false);
   const [open, setOpen] = useState(false);
   const bodyId = useId();

   useEffect(() => {
      const element = body.current;
      // Unfolded, scrollHeight and clientHeight agree whatever the length,
      // so re-measuring then would retract the control that got us here.
      if (element && !open) {
         setOverruns(element.scrollHeight > element.clientHeight + 1);
      }
   }, [html, open]);

   return (
      <>
         <div
            id={bodyId}
            ref={body}
            className={cn(
               className,
               !open && `${fold} overflow-hidden`,
               // The fold lands wherever 256px lands, through the middle
               // of a line as often as not; the fade says the words go on.
               // One mask-image rather than Tailwind's mask-b-* pair, which
               // registers 11 @property rules in the stylesheet every React
               // page loads: 2.9KB for this one fade, against 173 bytes.
               !open && overruns && '[mask-image:linear-gradient(to_bottom,black_80%,transparent)]'
            )}
            dangerouslySetInnerHTML={{ __html: html }}
         />
         {overruns && (
            <button
               type="button"
               aria-expanded={open}
               aria-controls={bodyId}
               onClick={() => setOpen(!open)}
               className={DISCLOSURE_CLASS}
            >
               {open ? _js('Show less') : _js('Show more')}
            </button>
         )}
      </>
   );
}

/**
 * A tally with two ends: it passes at the action's threshold and fails at
 * -2, so a bar that only fills toward passing would read a vote that is
 * being turned down as one that has stalled.
 *
 * Only while it is pending, though. ModeratorVoteLib sums pending rows
 * alone, so a settled vote scores zero however it went, and a meter here
 * would announce "no votes yet" over the list of people who cast them.
 * Those cards say how it ended instead.
 */
function VoteTally({ entry, viewerid }: { entry: ModerationEntry; viewerid: number | null }) {
   if (entry.status !== 'pending') {
      return <VoteOutcomeBadge entry={entry} />;
   }

   // The pips fill with the net, which is what the vote is decided on:
   // one against cancels one in favour, and the ends of the row are the
   // two thresholds. Counting each side instead put a lit pip next to an
   // empty one and promised a removal that was two votes away.
   //
   // The reader's own vote is marked on the pip their side has reached.
   // Where their side has reached nothing, because the other side has
   // cancelled them out, the mark is an empty ring on the first pip they
   // would light: their vote is on the board and it has moved nothing.
   const { inFavour, against } = sides(entry, viewerid);
   const mine = entry.myVote ?? 0;
   const ringAt =
      mine === 0
         ? null
         : Math.sign(mine) === Math.sign(entry.score)
           ? entry.score
           : Math.sign(mine);

   const failTone = sideTone(DOWN_VOTE, entry.destructive);
   const passTone = sideTone(UP_VOTE, entry.destructive);

   const failPips = [];
   for (let n = entry.failThreshold; n < 0; n++) {
      failPips.push(<Pip key={n} on={entry.score <= n} tone={failTone} mine={n === ringAt} />);
   }
   const passPips = [];
   for (let n = 1; n <= entry.passThreshold; n++) {
      passPips.push(<Pip key={n} on={entry.score >= n} tone={passTone} mine={n === ringAt} />);
   }

   return (
      // The pips hold together whatever the width; the words wrap under
      // them on a narrow card rather than pushing the row wider.
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
         <span className="flex shrink-0 items-center gap-1">
            {failPips}
            <i className="mx-1 h-3.5 w-px bg-gray-400" aria-hidden="true" />
            {passPips}
         </span>
         <span className="text-xs">
            <TallyLabel entry={entry} inFavour={inFavour} against={against} />
         </span>
      </div>
   );
}

/**
 * How many people are on each side. The reader's own vote is held apart
 * from the others: an optimistic patch sets myVote before the refetch
 * carries the voter row back, so counting the rows alone would leave the
 * reader out of their own tally for a moment.
 */
function sides(
   entry: ModerationEntry,
   viewerid: number | null
): { inFavour: number; against: number } {
   const others = entry.voters.filter(voter => voter.userid !== viewerid);
   const mine = entry.myVote;

   return {
      inFavour:
         others.filter(voter => voter.score > 0).length + (mine !== null && mine > 0 ? 1 : 0),
      against: others.filter(voter => voter.score < 0).length + (mine !== null && mine < 0 ? 1 : 0),
   };
}

function VoteOutcomeBadge({ entry }: { entry: ModerationEntry }) {
   return (
      <span className="flex shrink-0 flex-wrap items-center gap-2">
         <Badge className={cn('badge-md', entry.status === 'passed' ? 'badge-green' : 'badge-red')}>
            {entry.status === 'passed' ? _js('Passed') : _js('Failed')}
         </Badge>
         <span className="flex items-center gap-1.5 text-xs text-gray-500">
            {outcomeReason(entry)}
         </span>
      </span>
   );
}

/**
 * Why it ended that way, because the word on its own misleads: a vote can
 * only fail on its own by reaching the fail threshold, and almost none do.
 *
 * "Decided by a moderator" used to stand for every settled vote carrying
 * the forced flag, which on most failed votes is nobody: the two-week
 * cron stamps each row it closes. Those say what actually happened to
 * them, and the ones a person decided, either way, name the person.
 */
function outcomeReason(entry: ModerationEntry): ReactNode {
   if (entry.decidedBy) {
      return (
         <>
            {entry.status === 'failed' ? _js('Turned down by') : _js('Carried out by')}
            <VoterChip voter={entry.decidedBy} />
         </>
      );
   }
   if (entry.score <= entry.failThreshold) {
      return _js('Voted down');
   }
   if (entry.expired) {
      return _js('Nobody voted in two weeks');
   }
   if (entry.status === 'failed') {
      return _js('Closed by another decision');
   }

   return ___p(entry.score, 'Passed with %1 vote', 'Passed with %1 votes', String(entry.score));
}

/**
 * The action, as a shape. The queue mixes removals in with
 * reclassifications and with accepting an answer, and until you read the
 * heading nothing said which of them a card was: every card is the same
 * card, and the pips are the same length wherever two actions share a
 * threshold. One glyph at the start of the heading separates them before
 * the reading starts.
 *
 * Keyed off the action rather than a family. Three families were the
 * first idea, and on the queue as it stands four of the seven pending
 * votes are "accept an answer", which is neither a reclassification nor
 * a removal: the majority would have landed in the leftovers bin.
 *
 * The words stay in the heading. An uppercase family label above it
 * repeated the heading's own first word on 13 of the 23 headings
 * VoteCopy can produce, and cost a line on every card.
 */
const ACTION_ICONS: Record<string, string> = {
   switch_answer_to_question_vote: 'right-left',
   switch_answer_to_comment_vote: 'right-left',
   switch_answer_to_update_vote: 'right-left',
   comment_to_question_vote: 'right-left',
   comment_to_answer_vote: 'right-left',
   comment_to_update_vote: 'right-left',
   accept_an_answer_vote: 'circle-check',
   close_a_question_vote: 'circle-xmark',
   reopen_post_vote: 'rotate-left',
   reopen_comment_vote: 'rotate-left',
   reopen_story_vote: 'rotate-left',
   lock_post_vote: 'lock',
   unlock_post_vote: 'lock-open',
   mark_duplicate_vote: 'code-merge',
   unmark_duplicate_vote: 'split',
   migrate_vote: 'folder-arrow-up',
   delete_bad_answer_vote: 'trash',
   delete_bad_question_vote: 'trash',
   delete_spam_post_vote: 'trash',
   remove_spam_comment_vote: 'trash',
   remove_duplicate_off_topic_vote: 'trash',
   remove_comment_vote: 'trash',
   delete_and_punish_post_vote: 'trash-xmark',
   mark_spam_vote: 'trash-xmark',
   mark_offensive_vote: 'flag',
};

/** An action nobody has written words for. VoteCopy counts those
 *  destructive, so this draws in the destructive colour and says as much
 *  as anyone knows about it. */
const UNKNOWN_ACTION_ICON = 'circle-question';

function ActionIcon({ action, destructive }: { action: string; destructive: boolean }) {
   return (
      // Decorative: the heading beside it is the action, in words.
      <span
         aria-hidden="true"
         className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-xl',
            // red-50 is a fainter step off white than brand-50 (1.09
            // against 1.15), so the two tiles did not read at the same
            // weight. red-100 lands at 1.22, a shade stronger than the
            // brand tile, which is the right way round for a removal.
            destructive ? 'bg-red-100 text-red-700' : 'bg-brand-50 text-brand-700'
         )}
      >
         <FaIcon name={ACTION_ICONS[action] ?? UNKNOWN_ACTION_ICON} className="size-4" />
      </span>
   );
}

/** What a mark on this card means: the pips, the ring on the reader's own
 *  pip, the two places that say which way they went, and the ground a card
 *  closes onto once they have. */
const VOTE_TONES = {
   destructive: {
      fill: 'bg-red-600',
      ring: 'outline-red-700',
      text: 'text-red-700',
      // Against white, since the face inside carries a white border:
      // red-500 3.76:1, brand-500 4.94:1, gray-500 4.83:1.
      avatarRing: 'ring-red-500',
      surface: 'border-red-200 bg-red-50',
   },
   constructive: {
      fill: 'bg-brand-500',
      ring: 'outline-brand-600',
      text: 'text-brand-700',
      avatarRing: 'ring-brand-500',
      // The same informing fill the action tile spends, not decoration:
      // it says which way the reader went. gray-600 reads 6.55:1 on it.
      surface: 'border-brand-200 bg-brand-50',
   },
   neutral: {
      fill: 'bg-gray-500',
      ring: 'outline-gray-600',
      text: 'text-gray-700',
      avatarRing: 'ring-gray-500',
      // A step darker than the gray-50 page, and a gray-300 border, so a
      // card turning nothing down still reads as a card.
      surface: 'border-gray-300 bg-gray-100',
   },
} as const;

/** Voting for a removal is not good news, so the in-favour side wears what
 *  the vote carries out. Turning one down is nobody's alarm. */
function sideTone(score: number, destructive: boolean): keyof typeof VOTE_TONES {
   if (score <= 0) {
      return 'neutral';
   }

   return destructive ? 'destructive' : 'constructive';
}

function Pip({
   on,
   tone,
   mine = false,
}: {
   on: boolean;
   tone: keyof typeof VOTE_TONES;
   mine?: boolean;
}) {
   const label = on ? _js('Your vote') : _js('Your vote, cancelled out');
   const pip = (
      <i
         className={cn(
            'size-2.5 rounded-sm',
            on ? VOTE_TONES[tone].fill : mine ? 'bg-transparent' : 'bg-gray-200',
            mine && cn('outline-2 outline-offset-1', VOTE_TONES[tone].ring)
         )}
         aria-hidden="true"
      />
   );

   if (!mine) {
      return pip;
   }

   return (
      <Tooltip>
         <TooltipTrigger asChild>
            <span className="inline-flex items-center">
               {pip}
               <span className="sr-only">{label}</span>
            </span>
         </TooltipTrigger>
         <TooltipContent>{label}</TooltipContent>
      </Tooltip>
   );
}

/**
 * How far the vote has left to go, rather than how far it has come. "4 of
 * 5 to pass" made the reader subtract to learn their own click was the one
 * that decided it; the remainder says so directly, and when the reader is
 * the one who can cast it, so does the qualifier.
 */
function TallyLabel({
   entry,
   inFavour,
   against,
}: {
   entry: ModerationEntry;
   inFavour: number;
   against: number;
}) {
   if (inFavour + against === 0) {
      return <span className="text-gray-600">{_js('No votes yet')}</span>;
   }

   const { count, qualifier } = tallyWords(entry, inFavour, against);

   return (
      <>
         <span className="font-semibold tabular-nums text-gray-950">{count}</span>
         {qualifier !== null && <span className="text-gray-600"> · {qualifier}</span>}
      </>
   );
}

function tallyWords(
   entry: ModerationEntry,
   inFavour: number,
   against: number
): { count: string; qualifier: string | null } {
   // A net of zero is an empty row of pips whether nobody has voted or the
   // two sides have cancelled out, so the words tell them apart.
   if (entry.score === 0) {
      return {
         count: _js('%1 for, %2 against', String(inFavour), String(against)),
         qualifier: _js('contested'),
      };
   }

   const rejecting = entry.score < 0;
   const remaining = rejecting
      ? entry.score - entry.failThreshold
      : entry.passThreshold - entry.score;

   // The optimistic patch moves the tally the moment you click, so a card
   // sits at its threshold for the moment before the refetch takes it out
   // of the queue. "0 more votes" is not what that moment should say.
   if (remaining <= 0) {
      return {
         count: rejecting ? _js('Enough to reject it') : _js('Enough to pass'),
         qualifier: null,
      };
   }

   // Only worth saying to somebody who can actually cast it.
   const qualifier =
      remaining === 1 && entry.canVote && entry.myVote === null ? _js('yours settles it') : null;

   return {
      count: rejecting
         ? ___p(
              remaining,
              '%1 more vote to reject it',
              '%1 more votes to reject it',
              String(remaining)
           )
         : ___p(remaining, '%1 more vote to pass', '%1 more votes to pass', String(remaining)),
      qualifier,
   };
}

/** Where the reader stands on this vote, or why they cannot take one. */
function VoteState({
   entry,
   viewerid,
   voting,
}: {
   entry: ModerationEntry;
   viewerid: number | null;
   voting: VotingContext;
}) {
   if (entry.myVote !== null) {
      return (
         <span
            className={cn(
               'flex items-center gap-1.5 font-medium',
               VOTE_TONES[sideTone(entry.myVote, entry.destructive)].text
            )}
         >
            <FaIcon name="circle-check" className="size-3.5" />
            <span>{entry.myVote > 0 ? _js('You voted in favour') : _js('You voted against')}</span>
         </span>
      );
   }

   // Everything below answers "why can't I vote on this", which nobody
   // asks of a vote that is over. Settled rows all carry canVote false.
   if (entry.status !== 'pending') {
      return null;
   }

   // Whoever can vote reads the tally at the top of the card; saying it
   // again here would be the same sentence twice on one card.
   if (entry.canVote) {
      return null;
   }

   if (!voting.isLoggedIn) {
      return (
         <span className="text-gray-600">
            {_js('Anyone with %1 reputation or more can vote', String(voting.reputationThreshold))}
         </span>
      );
   }

   if (entry.subject.author?.userid === viewerid) {
      return <span className="text-gray-600">{_js('You cannot vote on your own content')}</span>;
   }

   // "Voting takes 20 reputation" read as a price: cast a vote, lose
   // twenty points. It is a doorway, and how far off it you are is the
   // part worth knowing.
   return (
      <span className="text-gray-600">
         {voting.viewerReputation === null
            ? _js('You need %1 reputation to vote', String(voting.reputationThreshold))
            : _js(
                 'You need %1 reputation to vote, and you have %2',
                 String(voting.reputationThreshold),
                 String(voting.viewerReputation)
              )}
      </span>
   );
}

/**
 * The two choices, named. A check and a cross would leave a voter to read
 * back up to the heading to learn what they were agreeing to, and the
 * queue mixes deletions with reclassifications.
 *
 * Once you have voted, the pair collapses to a withdrawal: which way you
 * went is on the strip beside it.
 */
function VoteActions({
   entry,
   voting,
   busy,
   onVote,
   onUndo,
   onCollapse,
}: {
   entry: ModerationEntry;
   voting: VotingContext;
   busy: boolean;
   onVote: (entry: ModerationEntry, score: number) => void;
   onUndo: (entry: ModerationEntry) => void;
   /** Present on a card the reader opened back up after voting. */
   onCollapse?: () => void;
}) {
   // The 44px floor below md matches the tabs and the filter; these are
   // the buttons a thumb actually works.
   const touch = 'max-md:min-h-11 max-md:flex-1';

   if (entry.myVote !== null) {
      return (
         <>
            {onCollapse && (
               <button
                  type="button"
                  className={cn('button-md button-outline', touch)}
                  onClick={onCollapse}
               >
                  {_js('Hide details')}
               </button>
            )}
            <button
               type="button"
               className={cn('button-md button-outline', touch)}
               disabled={busy}
               onClick={() => onUndo(entry)}
            >
               {_js('Undo my vote')}
            </button>
         </>
      );
   }

   if (!entry.canVote) {
      return voting.isLoggedIn ? null : (
         <a href={voting.loginUrl} className={cn('button-md button-primary no-underline', touch)}>
            {_js('Log in to vote')}
         </a>
      );
   }

   return (
      <>
         <button
            type="button"
            className={cn('button-md button-outline', touch)}
            disabled={busy}
            onClick={() => onVote(entry, DOWN_VOTE)}
         >
            {entry.failText}
         </button>
         <button
            type="button"
            // Solid brand invites; a removal takes the red outline
            // instead (DESIGN.md's Escalating-Destructive Rule).
            className={cn(
               'button-md',
               touch,
               entry.destructive
                  ? 'button-outline border-red-300 text-red-600 hover:border-red-400 hover:text-red-700'
                  : 'button-primary'
            )}
            disabled={busy}
            onClick={() => onVote(entry, UP_VOTE)}
         >
            {entry.passText}
         </button>
      </>
   );
}

/**
 * The one click nothing takes back, held apart from the queue. Voting is
 * ordinarily reversible from the card, so this only appears where it isn't.
 */
function ConfirmDecision({
   entry,
   superPowers,
   onOpenChange,
   onConfirm,
}: {
   entry: ModerationEntry | null;
   superPowers: boolean;
   onOpenChange: (open: boolean) => void;
   onConfirm: (entry: ModerationEntry) => void;
}) {
   return (
      <AlertDialog open={entry !== null} onOpenChange={onOpenChange}>
         <AlertDialogContent>
            <AlertDialogHeader>
               <AlertDialogTitle>{entry?.actionText}</AlertDialogTitle>
               <AlertDialogDescription>
                  {/* The one click nothing takes back names the post it
                      takes: the title above is the action, and ten cards
                      can share it. */}
                  {entry && (
                     <span className="mb-1 block font-medium text-gray-900">
                        {reference(entry)}
                     </span>
                  )}
                  <span>
                     {superPowers
                        ? _js('Super powers are on, so this happens as soon as you confirm.')
                        : _js(
                             'Yours is the vote that settles this, so it happens as soon as you confirm.'
                          )}
                  </span>
               </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
               <AlertDialogCancel>{_js('Cancel')}</AlertDialogCancel>
               <AlertDialogAction
                  className="bg-destructive-fill text-white hover:bg-destructive-fill/80 focus-visible:ring-destructive focus-visible:ring-offset-2"
                  onClick={() => entry && onConfirm(entry)}
               >
                  {entry?.actionText}
               </AlertDialogAction>
            </AlertDialogFooter>
         </AlertDialogContent>
      </AlertDialog>
   );
}

/**
 * Admins can carry out a vote on their own, which is what v1 called Super
 * Powers. It stays a deliberate switch rather than a third button: the
 * same two choices then decide the vote outright.
 *
 * Armed, it says so, because otherwise the only thing that changes on the
 * page is what the buttons do.
 */
function SuperPowers({
   enabled,
   onChange,
}: {
   enabled: boolean;
   onChange: (enabled: boolean) => void;
}) {
   return (
      <label
         className={cn(
            'flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm',
            enabled
               ? 'border-red-300 bg-red-50 font-medium text-red-700'
               : 'border-transparent text-gray-700 hover:bg-gray-100'
         )}
      >
         <Switch
            checked={enabled}
            onCheckedChange={onChange}
            // Armed is red, the same as the label around it: the switch
            // says a vote now decides, not that a setting is on.
            className="data-[state=checked]:bg-destructive"
         />
         <span>{enabled ? _js('Super powers on: your vote decides') : _js('Super powers')}</span>
      </label>
   );
}

/**
 * v1's keys, kept so the people who work this queue every week don't have
 * to relearn it: W and S walk the list, the arrows do the same, P and F
 * cast the vote, ? opens the list. E is gone with the toggle it opened,
 * since the flagged post is on the card now.
 */
function useQueueShortcuts({
   entries,
   enabled,
   onVote,
   onHelp,
}: {
   entries: ModerationEntry[];
   enabled: boolean;
   onVote: (entry: ModerationEntry, score: number) => void;
   onHelp: () => void;
}) {
   // The card the ring is on, by its vote rather than its place in the
   // list: voting reorders the queue, since it sorts by the date of the
   // last vote, so the card you just decided climbs to the top and a
   // remembered position would leave the ring over a post you never
   // picked. Null until W or S puts it on screen, because an outlined card
   // on arrival reads as a selection nobody made.
   const [focusedId, setFocusedId] = useState<number | null>(null);
   const engaged = focusedId !== null;
   // A queue with the keys off must not show a ring: it says the card is
   // picked while P and F do nothing.
   const focused =
      enabled && focusedId !== null ? entries.findIndex(entry => entry.voteid === focusedId) : -1;

   useEffect(() => {
      if (!enabled) {
         return;
      }

      const onKeyDown = (event: KeyboardEvent) => {
         if (isShortcutClaimed(event) || event.metaKey || event.ctrlKey || event.altKey) {
            return;
         }

         const key = event.key.toLowerCase();
         if (key === '?') {
            event.preventDefault();
            onHelp();
            return;
         }

         if (key === 'escape' && engaged) {
            setFocusedId(null);
            return;
         }

         const arrow = key === 'arrowdown' || key === 'arrowup';
         // Unasked, the arrows are how a keyboard reader scrolls; W or S
         // claims them for the queue and Escape hands them back.
         if (arrow && !engaged) {
            return;
         }

         const step =
            key === 's' || key === 'arrowdown' ? 1 : key === 'w' || key === 'arrowup' ? -1 : 0;
         if (step !== 0) {
            event.preventDefault();
            // The first press only brings the ring on screen, at the top,
            // and so does a press after the card it was on has left the
            // page.
            const next =
               focused < 0 ? 0 : Math.min(Math.max(focused + step, 0), entries.length - 1);
            setFocusedId(entries[next] ? entries[next].voteid : null);
            return;
         }

         const score = key === 'p' ? UP_VOTE : key === 'f' ? DOWN_VOTE : 0;
         const entry = entries[focused];
         if (score !== 0 && entry && entry.canVote && entry.myVote === null) {
            event.preventDefault();
            onVote(entry, score);
         }
      };

      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
   }, [enabled, engaged, entries, focused, onVote, onHelp]);

   return { focused, focusOn: setFocusedId };
}

function ShortcutsDialog({
   open,
   onOpenChange,
}: {
   open: boolean;
   onOpenChange: (open: boolean) => void;
}) {
   return (
      <Dialog open={open} onOpenChange={onOpenChange}>
         <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogTitle className="mb-2">{_js('Keyboard shortcuts')}</DialogTitle>
            <DialogDescription className="sr-only">
               {_js('A reference list of keyboard shortcuts available while moderating.')}
            </DialogDescription>
            <ShortcutList
               title={_js('The queue')}
               shortcuts={[
                  ['W', _js('Move up the queue')],
                  ['S', _js('Move down the queue')],
                  ['↑ ↓', _js('The same, once W or S has started you off')],
                  ['Esc', _js('Give the arrows back to the page')],
               ]}
            />
            <ShortcutList
               title={_js('Voting')}
               shortcuts={[
                  ['P', _js('Vote for what the flag proposes')],
                  ['F', _js('Vote against it')],
                  ['?', _js('Open this help')],
               ]}
            />
         </DialogContent>
      </Dialog>
   );
}

function voteErrorMessage(error: unknown): string {
   return error instanceof Error && error.message
      ? error.message
      : _js('That vote could not be recorded.');
}

/**
 * Who is on each side, as two counts rather than one run of chips. A
 * contested vote used to print eight names with +/- suffixes and became
 * the longest line on the card, which is exactly the visual activity the
 * queue was being criticised for. The names are still one click away, and
 * every card keeps this shape whether it is contested or not.
 */
function Voters({
   entry,
   viewerid,
   destructive,
}: {
   entry: ModerationEntry;
   viewerid: number | null;
   /** Which colour the in-favour side wears, the same rule as the pips. */
   destructive: boolean;
}) {
   const [open, setOpen] = useState(false);
   const panelId = useId();
   const { inFavour, against } = sides(entry, viewerid);

   // Counted rather than measured off entry.voters: an optimistic vote
   // sets myVote before the refetch carries the row back, and a line that
   // drew nobody straight after your own click would be lying. With nobody
   // on either side there is nothing to draw: the tally at the top of the
   // card already says "No votes yet", and a settled card's outcome line
   // says how it ended.
   if (inFavour + against === 0) {
      return null;
   }

   const mine = entry.myVote;
   const myRow = entry.voters.find(voter => voter.userid === viewerid);
   const others = entry.voters.filter(voter => voter.userid !== viewerid);
   // The reader leads their own side.
   const rowsFor = (side: number) => [
      ...(mine !== null && Math.sign(mine) === side && myRow ? [myRow] : []),
      ...others.filter(voter => Math.sign(voter.score) === side),
   ];

   return (
      <>
         {/* The words moved out: "1 more vote to pass" at the top of the
             card and "4 in favour, nobody against" down here were two
             readings of the same number. The faces keep the sides, each
             ringed in its own, and the counts stay for a reader who is
             not getting the colour. */}
         <span className="flex items-center gap-2">
            <VoterSide
               count={inFavour}
               label={
                  inFavour === 0
                     ? _js('nobody in favour')
                     : ___p(inFavour, '%1 vote in favour', '%1 votes in favour', String(inFavour))
               }
               voters={rowsFor(1)}
               ring={VOTE_TONES[sideTone(UP_VOTE, destructive)].avatarRing}
            />
            {inFavour > 0 && against > 0 && (
               <i className="h-3.5 w-px bg-gray-300" aria-hidden="true" />
            )}
            <VoterSide
               count={against}
               label={
                  against === 0
                     ? _js('nobody against')
                     : ___p(against, '%1 vote against', '%1 votes against', String(against))
               }
               voters={rowsFor(-1)}
               ring={VOTE_TONES[sideTone(DOWN_VOTE, destructive)].avatarRing}
            />
         </span>
         <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen(!open)}
            className={cn(DISCLOSURE_CLASS, 'mt-0')}
         >
            {open ? _js('Hide voters') : _js('Show voters')}
         </button>
         {/* The counts are on the line above, so these name the side only. */}
         <div id={panelId} hidden={!open} className="w-full pt-2 max-md:order-none md:order-last">
            <VoterList
               label={_js('In favour')}
               voters={rowsFor(1)}
               myVote={mine !== null && mine > 0 ? mine : null}
               myRow={myRow}
            />
            <VoterList
               label={_js('Against')}
               voters={rowsFor(-1)}
               myVote={mine !== null && mine < 0 ? mine : null}
               myRow={myRow}
            />
         </div>
      </>
   );
}

/**
 * One side, as its faces. The count is what a screen reader gets and
 * what survives if the ring colour does not reach the reader, so it is
 * spoken rather than drawn; the ring is what separates the sides for
 * everyone else.
 */
function VoterSide({
   count,
   label,
   voters,
   ring,
}: {
   count: number;
   label: string;
   voters: ModerationVoter[];
   ring: string;
}) {
   // Your own vote reaches the count before the refetch carries its row
   // back, so a side can have somebody on it and no face to draw.
   const faceless = count > 0 && voters.length === 0;

   return (
      <span className="flex items-center gap-1.5">
         <span className={faceless ? undefined : 'sr-only'}>{label}</span>
         {count > 0 && !faceless && <AvatarStack voters={voters} ring={ring} />}
      </span>
   );
}

/** Decorative: the count beside it is what carries the meaning. The
 *  white border is what keeps overlapping faces apart; the ring outside
 *  it is the side they voted on. */
function AvatarStack({ voters, ring }: { voters: ModerationVoter[]; ring: string }) {
   const shown = voters.slice(0, 5);
   const rest = voters.length - shown.length;

   return (
      <span className="flex items-center" aria-hidden="true">
         {shown.map(voter => (
            <img
               key={voter.userid}
               src={voter.avatar}
               alt=""
               title={voter.name}
               className={cn(
                  '-ml-[5px] size-5 shrink-0 rounded-full border-2 border-white object-cover ring-2 first:ml-0',
                  ring
               )}
               loading="lazy"
            />
         ))}
         {rest > 0 && (
            <span className="ml-1 tabular-nums text-gray-600">{_js('+%1', String(rest))}</span>
         )}
      </span>
   );
}

/** One side's names, behind the disclosure: where the chips went. */
function VoterList({
   label,
   voters,
   myVote,
   myRow,
}: {
   label: string;
   voters: ModerationVoter[];
   /** Set when this is the side the reader voted on. */
   myVote: number | null;
   myRow: ModerationVoter | undefined;
}) {
   // The optimistic patch sets myVote and leaves the voter rows alone
   // until the refetch lands, so your own side has a count and no rows
   // for that moment. The chip draws without a row; only an empty side
   // you are not on has nothing to show.
   if (voters.length === 0 && myVote === null) {
      return null;
   }

   const others = myVote === null ? voters : voters.filter(voter => voter !== myRow);

   return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-1">
         <span className="text-gray-600">{label}</span>
         {myVote !== null && (
            // Brand rather than the green or red it used to wear: these
            // chips are grouped by side now, and on a removal card red
            // is the in-favour count on the line above, so a red chip
            // under "In favour" would mean the opposite one line down.
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 py-0.5 pl-0.5 pr-2 font-semibold text-brand-900">
               <VoterAvatar voter={myRow} />
               {_js('You')}
            </span>
         )}
         {others.map(voter => (
            <VoterChip key={voter.userid} voter={voter} />
         ))}
      </div>
   );
}

/** A voter, small enough to sit in a line of them or beside an outcome.
 *  Which way they went is the heading they sit under. */
function VoterChip({ voter }: { voter: ModerationVoter }) {
   return (
      <a
         href={voter.url}
         className="inline-flex items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-0.5 pr-2 text-gray-700 no-underline hover:text-brand-500"
      >
         <VoterAvatar voter={voter} />
         {voter.name}
      </a>
   );
}

function VoterAvatar({ voter }: { voter: ModerationVoter | undefined }) {
   if (!voter) {
      return null;
   }

   return (
      <img
         src={voter.avatar}
         alt=""
         className="size-4 shrink-0 rounded-full object-cover"
         loading="lazy"
      />
   );
}

/**
 * This month's five busiest voters, the one thing on the page that says
 * somebody is working this queue. A card in the queue's own weight, and
 * each cell in the leaderboard's reading order: face and name on the
 * left, the count on the right. Below md the names drop and a face and a
 * number still rank five people; above it they fit, and the ones that
 * don't are on the title attribute.
 */
function TopVoters({
   voters,
   leaderboardUrl,
}: {
   voters: ModerationTopVoter[];
   leaderboardUrl: string;
}) {
   if (voters.length === 0) {
      return null;
   }

   return (
      <section className={cn(CARD_CLASS, 'mt-8')}>
         <header className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-2">
            <h2 className="m-0 text-sm font-semibold text-gray-900">{_js('Votes this month')}</h2>
            <a
               href={leaderboardUrl}
               className="text-sm font-medium text-brand-500 no-underline underline-offset-2 hover:text-brand-600 hover:underline"
            >
               {_js('Leaderboard')}
            </a>
         </header>
         <ul className="m-0 grid list-none grid-cols-3 divide-x divide-gray-200 p-0 md:grid-cols-5">
            {voters.map((voter, rank) => (
               <li
                  key={voter.userid}
                  className={cn('min-w-0 px-2 py-2 md:px-3', rank > 2 && 'max-md:hidden')}
               >
                  <a
                     href={voter.url}
                     title={voter.name}
                     className="flex items-center gap-2 no-underline"
                  >
                     <img
                        src={voter.avatar}
                        alt=""
                        className="size-6 shrink-0 rounded-full object-cover"
                        loading="lazy"
                     />
                     <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
                        {voter.name}
                     </span>
                     <span className="ml-auto shrink-0 text-sm font-bold tabular-nums text-gray-950">
                        {voter.votes}
                     </span>
                  </a>
               </li>
            ))}
         </ul>
      </section>
   );
}

function QueueTabs({
   queues,
   activeQueue,
   viewUrl,
   onSelect,
}: {
   queues: ModerationProps['queues'];
   /** null on the permalink, which is none of the queues. */
   activeQueue: ModerationQueueKey | null;
   viewUrl: (queue: ModerationQueueKey) => string;
   /** Absent on the single-vote permalink, where a tab is a real
    *  navigation rather than a switch between lists of the same shape. */
   onSelect?: (queue: ModerationQueueKey) => void;
}) {
   return (
      <nav className="flex flex-wrap gap-1">
         {queues.map(queue => (
            <a
               key={queue.key}
               href={viewUrl(queue.key)}
               aria-current={queue.key === activeQueue ? 'page' : undefined}
               onClick={event => {
                  if (!onSelect || isModifiedClick(event)) {
                     return;
                  }
                  event.preventDefault();
                  onSelect(queue.key);
               }}
               // max-md:min-h-11 rather than a taller strip everywhere: the
               // 44px target is a thumb's, and below md is where thumbs are.
               className={cn(
                  'inline-flex items-center rounded-lg px-3 py-1.5 text-sm no-underline max-md:min-h-11',
                  FOCUS_RING,
                  queue.key === activeQueue
                     ? 'bg-gray-200 font-medium text-gray-950'
                     : 'text-gray-600 hover:bg-gray-100'
               )}
            >
               {queue.label}
            </a>
         ))}
      </nav>
   );
}

/**
 * Which language's posts the queues show, defaulting to the subdomain's
 * own: fr.ifixit.com's queue was almost all English, which is backwards
 * for the people who moderate there. The menu offers the view language,
 * every language, and then whichever languages have pending posts, each
 * with its count so a moderator can see where help is needed. A post
 * whose language the model could not detect is binned as English, so no
 * choice ever hides a post from every queue.
 */
function LanguageFilter({
   langFilter,
   viewLanguage,
   otherLanguages,
   onSelect,
}: {
   langFilter: ModerationLangFilter;
   viewLanguage: ModerationProps['viewLanguage'];
   otherLanguages: ModerationProps['otherLanguages'];
   onSelect: (langFilter: ModerationLangFilter) => void;
}) {
   return (
      <label className="flex items-center gap-2 text-sm text-gray-700">
         {_js('Show')}
         {/* Live on a permalink too: a pick runs goTo, a deliberate exit
             to the queue, the same trip the tab links make. */}
         <Select value={langFilter} onValueChange={onSelect}>
            {/* The wrapping label never names a combobox: HTML-AAM
                takes aria-label, then the subtree, which is only the
                picked value. Without this it announces "English,
                combobox" and nothing says what it filters. */}
            <SelectTrigger aria-label={_js('Show language')} className="h-9 w-56 bg-white">
               <SelectValue />
            </SelectTrigger>
            <SelectContent>
               <SelectItem value={viewLanguage.key}>{viewLanguage.label}</SelectItem>
               <SelectItem value={ALL_LANGUAGES}>{_js('All languages')}</SelectItem>
               {otherLanguages.length > 0 && (
                  <>
                     <SelectSeparator />
                     <SelectGroup>
                        <SelectLabel>{_js('Languages with pending posts')}</SelectLabel>
                        {otherLanguages.map(language => (
                           /* ItemText shrink-wraps; without w-full the
                              count's ml-auto has nothing to push against. */
                           <SelectItem
                              key={language.key}
                              value={language.key}
                              className="[&>span:last-child]:w-full"
                           >
                              {language.label}
                              {language.count !== null && (
                                 /* "0 pending", never a bare "0": on Passed
                                    or Failed that language still has posts. */
                                 <span className="ml-auto text-xs tabular-nums text-gray-500">
                                    {_js('%1 pending', String(language.count))}
                                 </span>
                              )}
                           </SelectItem>
                        ))}
                     </SelectGroup>
                  </>
               )}
            </SelectContent>
         </Select>
      </label>
   );
}

/** One box rather than an All / Not voted by me pair: the filter has two
 *  states and one of them is the default, which is what a checkbox says. */
function VotedFilter({
   filter,
   onSelect,
}: {
   filter: ModerationFilter;
   onSelect: (filter: ModerationFilter) => void;
}) {
   return (
      <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-3 text-sm text-gray-700 hover:bg-gray-100">
         <Checkbox
            checked={filter === 'hideMine'}
            onCheckedChange={hide => onSelect(hide ? 'hideMine' : 'all')}
            className={cn('size-4 cursor-pointer rounded-sm accent-brand-500', FOCUS_RING)}
         />
         <span>{_js('Hide my votes')}</span>
      </label>
   );
}

function QueuePagination({
   view,
   pageData,
   pageUrl,
   onPageChange,
}: {
   view: ModerationView;
   pageData: ModerationPageData;
   pageUrl: (page: number) => string;
   onPageChange: (page: number) => void;
}) {
   const page = view.page;
   const pageCount = Math.max(1, Math.ceil(pageData.total / pageData.perPage));
   if (pageCount <= 1) {
      return null;
   }

   const range = calculatePageRange(page, pageCount, 5);
   const pages = Array.from(
      { length: range.end - range.start + 1 },
      (_unused, offset) => range.start + offset
   );
   const goTo = (event: ReactMouseEvent, target: number) => {
      if (isModifiedClick(event)) {
         return;
      }
      event.preventDefault();
      if (target >= 1 && target <= pageCount && target !== page) {
         onPageChange(target);
      }
   };

   return (
      <div className="mt-4">
         <Pagination>
            <PaginationContent className="list-none pl-0">
               <PaginationItem>
                  <PaginationPrevious
                     href={pageUrl(page - 1)}
                     aria-disabled={page === 1}
                     className={cn('no-underline', page === 1 && 'pointer-events-none opacity-50')}
                     onClick={event => goTo(event, page - 1)}
                  />
               </PaginationItem>
               {pages.map(target => (
                  <PaginationItem key={target}>
                     <PaginationLink
                        href={pageUrl(target)}
                        isActive={target === page}
                        className="no-underline"
                        onClick={event => goTo(event, target)}
                     >
                        {target}
                     </PaginationLink>
                  </PaginationItem>
               ))}
               <PaginationItem>
                  <PaginationNext
                     href={pageUrl(page + 1)}
                     aria-disabled={page === pageCount}
                     className={cn(
                        'no-underline',
                        page === pageCount && 'pointer-events-none opacity-50'
                     )}
                     onClick={event => goTo(event, page + 1)}
                  />
               </PaginationItem>
            </PaginationContent>
         </Pagination>
      </div>
   );
}

/** The card's own shape: the action heading and its tally, the question,
 *  the flagged post, and the line of voters under them. */
function QueueSkeleton({ rows = 3 }: { rows?: number }) {
   return (
      <ul aria-hidden="true" className="m-0 grid list-none gap-8 p-0">
         {Array.from({ length: Math.min(Math.max(rows, 1), 10) }, (_unused, row) => (
            <li key={row} className={cn(CARD_CLASS, 'space-y-3 p-4')}>
               <div className="flex items-center justify-between gap-4">
                  <Skeleton className="h-5 w-56" />
                  <Skeleton className="h-4 w-28" />
               </div>
               <Skeleton className="h-16 w-full rounded-xl" />
               <Skeleton className="h-24 w-full rounded-xl" />
               <Skeleton className="h-4 w-64" />
            </li>
         ))}
      </ul>
   );
}

/** behavior:'smooth' is an explicit request that the preference does not
 *  override on its own. */
function prefersReducedMotion(): boolean {
   return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** Modified clicks keep the native link behavior: new tab, new window. */
function isModifiedClick(event: ReactMouseEvent): boolean {
   return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

/**
 * The queue, its page, its language and the unvoted filter all live in
 * the URL, the way they did when each was a page of its own:
 * /Moderation/Passed/3, and ?filter=hideMine or ?langFilter=de on top.
 * Switching any of them no longer reloads the page, it pushes a history
 * entry and refetches, and Back reads the view straight back out of the
 * address bar.
 */
function useQueueView(props: ModerationProps): [ModerationView, (view: ModerationView) => void] {
   const [view, setViewState] = useState<ModerationView>({
      queue: props.activeQueue,
      page: props.initialData.page,
      filter: props.initialData.filter,
      langFilter: props.initialData.langFilter,
      voteid: props.voteid,
   });

   useEffect(() => {
      const onPopState = () => setViewState(current => viewFromLocation(props, current));
      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
   }, [props]);

   const setView = (next: ModerationView) => {
      // Same view, no write: clicking the queue you are already on, or the
      // filter already pressed, would otherwise bury the real previous
      // view under duplicate history entries and leave Back doing nothing.
      if (
         next.queue === view.queue &&
         next.page === view.page &&
         next.filter === view.filter &&
         next.langFilter === view.langFilter &&
         next.voteid === view.voteid
      ) {
         return;
      }

      setViewState(next);
      window.history.pushState(null, '', viewUrl(props, next));
   };

   return [view, setView];
}

function viewUrl(props: ModerationProps, view: ModerationView): string {
   const base = props.queues.find(queue => queue.key === view.queue)?.url ?? '/Moderation';
   const path = view.page > 1 ? `${base}/${view.page}` : base;

   const params = new URLSearchParams();
   if (view.filter === 'hideMine') {
      params.set('filter', 'hideMine');
   }
   // The default rides as no param at all, so the plain /Moderation link
   // keeps meaning "your language" wherever it is pasted.
   if (view.langFilter !== props.viewLanguage.key) {
      params.set(LANGUAGE_FILTER_PARAM, view.langFilter);
   }

   const query = params.toString();
   return query === '' ? path : `${path}?${query}`;
}

function parseLangFilter(props: ModerationProps, raw: string | null): ModerationLangFilter {
   const known =
      raw === ALL_LANGUAGES || props.otherLanguages.some(language => language.key === raw);

   return known && raw !== null ? raw : props.viewLanguage.key;
}

function viewFromLocation(props: ModerationProps, fallback: ModerationView): ModerationView {
   const path = window.location.pathname;
   const params = new URLSearchParams(window.location.search);

   // A permalink's trailing number is the vote, not a page of the queue;
   // only the server writes these URLs, so it is the arrival's.
   const voteMatch = path.match(/\/Vote\/(\d+)$/);
   if (voteMatch) {
      return {
         queue: props.activeQueue,
         page: props.initialData.page,
         filter: props.initialData.filter,
         langFilter: props.initialData.langFilter,
         voteid: Number(voteMatch[1]),
      };
   }

   const page = Number(path.match(/\/(\d+)$/)?.[1] ?? 1);
   const base = path.replace(/\/\d+$/, '');
   // Longest first: /Moderation is a prefix of every other queue's URL.
   const queue = [...props.queues]
      .sort((left, right) => right.url.length - left.url.length)
      .find(candidate => candidate.url === base);
   const filter = params.get('filter') === 'hideMine' ? 'hideMine' : 'all';
   const langFilter = parseLangFilter(props, params.get(LANGUAGE_FILTER_PARAM));

   return { queue: queue ? queue.key : fallback.queue, page, filter, langFilter, voteid: null };
}
